import classNames from "classnames"
import { get, set } from "nodash"
import { SyntheticEvent, useCallback } from "react"
import { DefaultJsxChildrenProp } from "themes/default-tailwind/types"
import { FormSubmitHandlerFunction } from "./types"

type FormValuesMap = Record<string, string>

type CompactAuthenticationManagementFormProps = {
    children: DefaultJsxChildrenProp
    classes?: string
    submitHandler?: FormSubmitHandlerFunction<FormValuesMap>
}

/**
 * a VERY BASIC component wrapper that provides a simple way to collect input field values as strings that are passed to submitHandler
 * for any more complex validation/value parsing, this will require some enhancements!
 */
export default function CompactAuthenticationManagementForm({
    children,
    classes,
    submitHandler,
}: CompactAuthenticationManagementFormProps) {
    const handleSubmit = useCallback(
        (e: SyntheticEvent) => {
            if (typeof submitHandler !== "function") {
                return
            }

            e.preventDefault()

            // the elements also contains buttons, so we filter them by their name attribute, as buttons generally don't carry that
            // see this reference for what it is: https://developer.mozilla.org/en-US/docs/Web/API/HTMLFormControlsCollection
            const result = Array.from(
                get(e, "target.elements") as HTMLFormControlsCollection
            ).reduce((result, element) => {
                const { value, name, checked, type } = element as HTMLInputElement
                if (type === "checkbox") {
                    set(result, name, checked)
                    return result
                }
                if (name) {
                    set(result, name, value)
                }

                return result
            }, {} as FormValuesMap)

            submitHandler(result)
        },
        [submitHandler]
    )

    return (
        <div className={classNames("w-full", classes)}>
            <form
                method="post"
                data-tid="authentication-form"
                className="flex flex-col place-content-stretch gap-y-4 text-black-400"
                // let the parent components handle this via onChange state setters that collect data for submit buttonsx
                onSubmit={handleSubmit}
            >
                {children}
            </form>
        </div>
    )
}
